import React from 'react';
import { motion } from 'framer-motion';
import { BookOpenIcon, CheckCircle2Icon, CreditCardIcon, DownloadIcon, LockIcon, ZapIcon } from 'lucide-react';
import { CTAButton } from './Shared';
import { Blossom, DropperBottle, Sprig } from './FloralDecor';
const productImage = 'https://cdn.magicpatterns.com/uploads/mo1ks7W9YKSLHJ5r95X3xT/ChatGPT_Image_25_ago_2026,_15_34_51.png';
const stats = [{
  icon: DownloadIcon,
  text: 'Listo para imprimir o usar en tablet'
}, {
  icon: LockIcon,
  text: 'Pago 100% seguro con tarjeta o PayPal'
}, {
  icon: ZapIcon,
  text: 'Acceso en 5 minutos'
}];
export function Hero() {
  return <header className="relative overflow-hidden bg-brand-greenDeep pb-10 pt-8 text-brand-cream md:pb-20 md:pt-16" data-id="element-110" data-mp="s9zybf8tt2lo">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden" data-id="element-111" data-mp="nbosrgpkde99">
        <Sprig className="animate-float-slow absolute -left-6 top-6 h-72 w-44 text-brand-sage/30 md:h-96 md:w-56" data-id="element-112" data-mp="3d33f4zea6vla" />
        <Sprig className="animate-float-soft absolute -right-8 top-24 h-72 w-44 -scale-x-100 text-brand-sage/25 md:h-[26rem] md:w-56" data-id="element-113" data-mp="384jud2bfbpui" />
        <Blossom className="absolute left-[12%] top-[52%] h-16 w-16 text-brand-rose/40 md:h-24 md:w-24" data-id="element-114" data-mp="3o85b7b3xi14k" />
        <Blossom className="absolute right-[14%] top-[18%] h-12 w-12 text-brand-amber/40 md:h-16 md:w-16" data-id="element-115" data-mp="39authk0ozz0e" />
        <DropperBottle className="animate-float-soft absolute bottom-6 left-[6%] hidden h-40 w-20 text-brand-goldLight/25 lg:block" data-id="element-116" data-mp="3e8mt707kwpf5" />
        <DropperBottle className="animate-float-slow absolute bottom-10 right-[7%] hidden h-32 w-16 text-brand-sage/30 lg:block" data-id="element-117" data-mp="30gq47517ivmz" />
        <div className="absolute -top-24 left-1/2 h-72 w-[36rem] -translate-x-1/2 rounded-full bg-brand-greenMid/40 blur-[120px]" data-id="element-118" data-mp="2qjv96w457ddg" />
      </div>

      <div className="relative mx-auto max-w-7xl px-4 text-center sm:px-6 lg:px-8" data-id="element-119" data-mp="2vjzmkd9r7rzg">
        <motion.div initial={{
        opacity: 0,
        y: 18
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        ease: [0.23, 1, 0.32, 1]
      }} data-id="element-120" data-mp="2hix2g62bkrr6">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-brand-gold/50 bg-white/10 px-4 py-1.5 backdrop-blur-sm" data-id="element-121" data-mp="s3349bcaykwh">
            <span className="text-xs md:text-sm" data-id="element-122" data-mp="n9i53fyz3kvp">🔮</span>
            <span className="font-title text-[10px] font-bold tracking-[.18em] text-brand-goldLight md:text-xs" data-id="element-123" data-mp="id03goyxw2nl">
              LUMEN AKASHA™
            </span>
          </div>

          <div className="mx-auto max-w-5xl" data-id="element-124" data-mp="1b1fd0vzaxsrj">
            <h1 className="neon-title font-serif text-[32px] font-bold leading-[1.08] tracking-tight md:text-[clamp(2.4rem,6.4vw,4.8rem)] md:leading-[1.02] md:tracking-[-0.035em]" data-id="element-125" data-mp="1b04aezhler20">
              <span className="block" data-id="element-126" data-mp="111ewfhsthqr1">Sesiones más seguras,</span>
              <span className="neon-accent block" data-id="element-127" data-mp="x1nvil9ktu8s">más profundas</span>
              <span className="block" data-id="element-128" data-mp="x3h0lw4jk6eo">y sin improvisar.</span>
            </h1>
            <div className="neon-underline mx-auto mt-5 w-40 md:w-64" aria-hidden="true" data-id="element-129" data-mp="3lu9x916uryb8" />
            <p className="mx-auto mt-5 max-w-2xl text-[14px] font-medium leading-relaxed text-brand-cream/80 md:text-lg" data-id="element-130" data-mp="3hpsglavsi14o">
              El conocimiento ya lo tienes. El sistema para aplicarlo con autoridad profesional y generar resultados
              tangibles en tus pacientes, ahora también.
            </p>
          </div>

          <div className="mx-auto mt-6 flex max-w-3xl justify-center md:mt-10" data-id="element-131" data-mp="26868q58azxc6">
            <motion.img src={productImage} alt="Botiquín Bach: manual de protocolos y sus siete láminas de fichas, fórmulas y registros" initial={{
            opacity: 0,
            scale: 0.96
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.3,
            ease: [0.23, 1, 0.32, 1]
          }} className="h-auto w-[84%] max-w-[620px] drop-shadow-2xl md:w-[92%]" draggable={false} fetchPriority="high" data-id="element-132" data-mp="2b20tzd3k4kdd" />
          </div>

          <div className="mx-auto mt-6 max-w-3xl space-y-3" data-id="element-133" data-mp="uou4pe6sk4dj">
            <h2 className="font-title text-lg font-bold tracking-wide text-brand-goldLight md:text-3xl" data-id="element-134" data-mp="zmc6mkme5jc1">
              BOTIQUÍN BACH COMPLETO™
            </h2>
            <div className="leaf-rule mx-auto max-w-xs" aria-hidden="true" data-id="element-135" data-mp="3vi6xmil2u6sm" />
            <p className="mx-auto max-w-xl font-serif text-[15px] font-medium leading-snug text-brand-cream/90 md:text-xl md:leading-normal" data-id="element-136" data-mp="3vk1n8a7amn31">
              🌼 Sistema profesional de protocolos y fórmulas de Flores de Bach, listo para aplicar desde tu próxima
              consulta.
            </p>
            <CTAButton variant="gold" className="cta-shimmer relative mt-3 overflow-hidden px-7 py-3.5 text-sm md:px-10 md:py-5 md:text-xl" data-id="element-137" data-mp="3hnxwwrzjiyvu">
              <span className="relative z-10" data-id="element-138" data-mp="3rh584tgyp93u">→ VER PACK</span>
            </CTAButton>
          </div>

          <div className="mx-auto mt-10 grid max-w-3xl gap-3 rounded-2xl border border-white/15 bg-white/5 p-5 backdrop-blur-sm sm:grid-cols-3" data-id="element-139" data-mp="3moc2829rbuq6">
            {stats.map(stat => {
            const Icon = stat.icon;
            return <div key={stat.text} className="flex flex-col items-center gap-2 text-center" data-id="element-140" data-mp="2u06gbc6yvzor">
                  <Icon className="h-6 w-6 text-brand-amber" data-id="element-141" data-mp="19h2qsjqfdm5g" />
                  <span className="text-xs leading-snug text-brand-cream/80 md:text-sm" data-id="element-142" data-mp="1e9vquuay86b7">{stat.text}</span>
                </div>;
          })}
          </div>

          <div className="mt-6 flex flex-wrap justify-center gap-2 text-[10px] font-semibold md:text-sm" data-id="element-143" data-mp="ziekgi7syvhl">
            <span className="rounded-full border border-white/20 bg-white/5 px-3 py-1.5 text-brand-cream/85" data-id="element-144" data-mp="14h7yqcuwyqit">
              <BookOpenIcon className="mr-1.5 inline h-3.5 w-3.5 text-brand-amber" data-id="element-145" data-mp="1x8834ox5pwb0" />
              100% PDF
            </span>
            <span className="rounded-full border border-white/20 bg-white/5 px-3 py-1.5 text-brand-cream/85" data-id="element-146" data-mp="1j98fnkldrngf">
              <CheckCircle2Icon className="mr-1.5 inline h-3.5 w-3.5 text-brand-amber" data-id="element-147" data-mp="1n90si9ht4frl" />
              Garantía 7 días
            </span>
            <span className="rounded-full border border-white/20 bg-white/5 px-3 py-1.5 text-brand-cream/85" data-id="element-148" data-mp="7veesav4wmla">
              <CreditCardIcon className="mr-1.5 inline h-3.5 w-3.5 text-brand-amber" data-id="element-149" data-mp="bsk8e2vb5aye" />
              Tarjeta y PayPal
            </span>
            <span className="rounded-full border border-brand-gold/50 bg-brand-amber/15 px-3 py-1.5 text-brand-goldLight" data-id="element-150" data-mp="29hxumfkfi4sr">
              🌿 El kit más completo en español
            </span>
          </div>
        </motion.div>
      </div>
    </header>;
}